/**
 * OPD prescription lookup — request shaping, response mapping, validation and
 * error formatting.
 *
 * Deliberately imports nothing from `api/`: `pharmacyMappers.js` reaches
 * `api/inventory-request` → `Axios-interceptor` → axios, which is ESM and cannot
 * be transformed by CRA's Jest. Keeping this module free of that chain is what
 * makes it unit-testable without mocking.
 */
import dayjs from 'dayjs';
import { formatDispensingError } from './dispensingEntity';

/** Stable codes the caller branches on. Message text is for humans, these are not. */
export const OPD_LOOKUP_ERROR = {
  /** Failed client-side validation — no request was made. Render on the field. */
  INVALID_QUERY: 'INVALID_QUERY',
  /** The request was made and failed. Render as a banner. */
  REQUEST_FAILED: 'REQUEST_FAILED',
};

/** First value that is neither null/undefined nor empty string; '' when there is none. */
const firstOf = (...values) => values.find((v) => v != null && v !== '') ?? '';

/** OPD/ABDM payloads deliver list-ish fields as an array, a bare scalar or null. */
function toOpdArray(value) {
  if (Array.isArray(value)) return value;
  return value == null || value === '' ? [] : [value];
}

/** Unparseable dates sort last rather than turning the comparator into NaN. */
function opdTimestamp(value) {
  const parsed = dayjs(value);
  return parsed.isValid() ? parsed.valueOf() : 0;
}

/**
 * Patient block flattened onto the row, so no component reads raw payload keys.
 * The phone/age/gender here end up on the pharmacy bill.
 */
export function mapOpdPatient(patient) {
  return {
    patient_id: firstOf(patient?.patient_id),
    name: firstOf(patient?.patient_name, patient?.name),
    gender: firstOf(patient?.patient_gender, patient?.gender),
    age: firstOf(patient?.patient_age, patient?.age),
    phone_no: firstOf(patient?.patient_phone_no, patient?.phone_no, patient?.phone),
    abha_id: firstOf(patient?.patient_abha_id, patient?.abha_id),
    abha_no: firstOf(patient?.patient_abha_no, patient?.abha_no),
    address: firstOf(patient?.patient_address, patient?.address),
    email: firstOf(patient?.patient_email, patient?.email),
  };
}

/** Prescribed medicines from an OPD prescription. `dose` and `dosage` both occur. */
export function mapOpdMedications(prescription) {
  return toOpdArray(prescription?.medications).map((med, index) => {
    const id = `opd-med-${index}`;
    if (!med || typeof med !== 'object') {
      return { id, name: String(med), dose: '', route: '', frequency: '', duration: '', instructions: '' };
    }
    return {
      id,
      name: firstOf(med.name, med.medicine, med.medicine_name),
      dose: firstOf(med.dose, med.dosage),
      route: firstOf(med.route),
      frequency: firstOf(med.frequency),
      duration: firstOf(med.duration),
      instructions: firstOf(med.instructions, med.note, med.notes),
    };
  });
}

/**
 * One `patient_prescriptions[]` entry → one flat row per prescription, newest first.
 * The payload carries no prescription id, so the row id is synthesised.
 */
export function mapOpdPrescriptions(patientEntry) {
  const rows = toOpdArray(patientEntry?.prescriptions).map((entry, index) => {
    const rx = entry?.prescription || {};
    const patient = mapOpdPatient(entry?.patient);
    const patientId = firstOf(patient.patient_id, patientEntry?.patient_id) || 'opd';
    const medications = mapOpdMedications(rx);
    return {
      id: `${patientId}-${index}`,
      datetime: firstOf(rx.datetime, rx.date),
      doctor_name: firstOf(entry?.practitioner?.name, entry?.practitioner?.id),
      facility_name: firstOf(entry?.facility?.name),
      diagnosis: firstOf(rx.diagnosis),
      chief_complaints: toOpdArray(rx.chief_complaints),
      clinical_notes: firstOf(rx.clinical_notes),
      medications,
      medicine_count: medications.length,
      patient,
    };
  });
  return rows.sort((a, b) => opdTimestamp(b.datetime) - opdTimestamp(a.datetime));
}

/**
 * `patient_prescriptions[]` → one selectable entry per patient.
 * A phone number can be shared, so more than one patient may come back; patients
 * with no prescriptions are dropped since there is nothing to dispense.
 */
export function mapOpdPatientEntries(patientPrescriptions) {
  return toOpdArray(patientPrescriptions)
    .map((entry, index) => {
      const rows = mapOpdPrescriptions(entry);
      return {
        key: firstOf(entry?.patient_id, index),
        patient: rows[0]?.patient || mapOpdPatient(null),
        rows,
      };
    })
    .filter((entry) => entry.rows.length > 0);
}

/**
 * Salt to search substitutes by. ABDM prescriptions carry no `generic_name` — only
 * free text — but they conventionally embed the salt in parentheses:
 *   "Azi 500mg Tablet (Azithromycin (500mg))"          → "Azithromycin"
 *   "Dol Proxyvon (tapentadol hydrochloride) 100 mg"   → "tapentadol hydrochloride"
 * Falls back to the trimmed name so the Substitute button is never dead.
 */
export function extractOpdGenericTerm(name) {
  const text = String(name || '').trim();
  if (!text) return '';
  const outer = text.match(/\(([^]*)\)/);
  if (!outer) return text;
  const salt = outer[1].replace(/\([^()]*\)/g, '').replace(/[()]/g, '').trim();
  // A parenthetical holding only a strength ("Dolo (650mg)") is not a salt —
  // searching it would return nothing, so search the full name instead.
  if (!salt || /^[\d.]+\s*(mg|ml|g|mcg|iu|%)$/i.test(salt)) return text;
  return salt;
}

/** ABHA addresses end with the environment's ABDM suffix (sandbox vs production). */
export function abhaAddressSuffix() {
  return process.env.REACT_APP_IS_ABDM_CONNECTED_WITH_PROD === 'true'
    ? process.env.REACT_APP_SUFFIX_PR
    : process.env.REACT_APP_SUFFIX_SB;
}

/** Search keys the endpoint accepts. All filled ones are ANDed server-side; `name` is a partial match. */
export const OPD_LOOKUP_FIELDS = ['name', 'phone_no', 'abha_address', 'patient_id'];

/** Partial name matches can return very large responses, so short names are not searched. */
export const OPD_NAME_MIN_LENGTH = 3;

/**
 * One search box → the endpoint field it should be sent as:
 *   10 digits                 → phone_no
 *   contains "@"              → abha_address
 *   letters and spaces only   → name (partial match)
 *   anything else (has digit) → patient_id (alphanumeric)
 * Returns {} for blank input.
 */
export function classifyLookupQuery(value) {
  const text = String(value ?? '').trim();
  if (!text) return {};
  if (/^\d{10}$/.test(text)) return { phone_no: text };
  if (text.includes('@')) return { abha_address: text };
  if (/^[\p{L}\s.'-]+$/u.test(text)) return { name: text };
  return { patient_id: text };
}

/** Trimmed, non-empty lookup fields only. */
export function pickLookupFields(fields) {
  return OPD_LOOKUP_FIELDS.reduce((acc, key) => {
    const value = String(fields?.[key] ?? '').trim();
    if (value) acc[key] = value;
    return acc;
  }, {});
}

/** Why the current fields cannot be searched, or '' when they can. */
export function lookupValidationMessage(fields) {
  const picked = pickLookupFields(fields);
  if (!Object.keys(picked).length) return 'Enter a name, phone number, ABHA address or patient ID';
  if (picked.name && picked.name.length < OPD_NAME_MIN_LENGTH) {
    return `Enter at least ${OPD_NAME_MIN_LENGTH} characters of the name`;
  }
  return '';
}

/** Inclusive calendar window sent on every OPD prescription lookup (last 7 days). */
export function opdLookupDateWindow(now = dayjs()) {
  const at = dayjs(now);
  return {
    from_date: at.subtract(7, 'day').format('YYYY-MM-DD'),
    to_date: at.format('YYYY-MM-DD'),
  };
}

/** Client-side safety net when the API ignores from_date/to_date. */
export function isWithinOpdLookupWindow(datetime, now = dayjs()) {
  const cutoff = dayjs(now).subtract(7, 'day').startOf('day');
  const at = dayjs(datetime);
  return at.isValid() && !at.isBefore(cutoff);
}

/**
 * Search fields → POST body (always includes last-7-day from_date/to_date), or null
 * when the fields fail validation.
 */
export function buildPrescriptionLookupBody(fields) {
  if (lookupValidationMessage(fields)) return null;
  return { ...pickLookupFields(fields), ...opdLookupDateWindow() };
}

/** True when axios never received a response — network drop, DNS failure or client timeout. */
function noResponseReceived(err) {
  return !err?.response && Boolean(err?.request || err?.isAxiosError);
}

/**
 * What the server actually said, or '' when the response carried no usable body.
 * Gated on the body existing because `formatDispensingError` otherwise falls
 * through to `err.message`, which the transport has already replaced with
 * generic text that tells a pharmacist nothing.
 */
function serverDetail(err) {
  const body = err?.apiResponse || err?.response?.data;
  if (!body || typeof body !== 'object') return '';
  const detail = formatDispensingError(err, '');
  return detail && detail !== err?.message ? detail : '';
}

/**
 * User-facing message for a failed lookup: connection, timeout, 4xx and 5xx each
 * get their own text, preferring whatever the server actually said.
 */
export function formatOpdPrescriptionError(err, fallback = 'Could not load prescriptions. Please try again.') {
  if (!err) return fallback;

  const status = err.status ?? err.response?.status;

  if (typeof navigator !== 'undefined' && navigator.onLine === false) {
    return 'You appear to be offline. Check your connection and try again.';
  }
  if (status == null && noResponseReceived(err)) {
    return 'Could not reach the server. It may be busy, or your connection dropped.';
  }
  if (status === 401 || status === 403) {
    return 'Your session is no longer authorised for this lookup. Sign in again and retry.';
  }
  if (status === 404) {
    return 'No prescription records exist for this patient.';
  }
  if (status === 408 || status === 504) {
    return 'The request timed out. Please try again.';
  }
  if (status === 429) {
    return 'Too many lookups in a short time. Please wait a moment and try again.';
  }
  if (typeof status === 'number' && status >= 500) {
    return serverDetail(err) || 'Server error. Please try again in a few moments.';
  }
  if (typeof status === 'number' && status >= 400) {
    return serverDetail(err) || 'That lookup was rejected. Check the number and try again.';
  }
  return serverDetail(err) || fallback;
}
